import auditSheetRepository from '../../../repositories/AuditSheetRepository';
import { createEmptyColumn } from '../utils/commonAuditSheet.utils';
import { arrangeData } from '../utils/arrangeAuditSheetData';
import { toTitleCase } from '../../../utils/common.utils';
import { format } from 'date-fns';
import { decodeResponseValue } from '../utils/responseColumn';

const OMITTING_KEYS = [
  'missing_info',
  'provider_rvu',
  'provider_dollar_value',
  'agree',
  'disagree',
  'audited_rvu',
  'audited_dollar_value',
  'icd_agree',
  'icd_disagree',
  'chart_id',
  'id',
  'row_id',
  'billedCptIcdMap',
  'auditedCptIcdMap'
];

const FLATTENING_KEYS = [
  'srvcs_no',
  'audited_cpt',
  'billed_modifier',
  'audited_modifier',
];

const STARTING_KEYS = ['file_name', 'encounter_no', 'rendering', 'enc_dt'];

export const FLATTENED_CSV_HEADERS = [
  'Billed CPT',
  'Audited CPT',
  'Billed Modifier',
  'Audited Modifier',
  'Units Billed',
  'Units Reviewed',
];

const COLUMN_ORDER = [
  { key: 'file_name',     header: 'File Id #'        },
  { key: 'encounter_no',  header: 'Encounter #'       },
  { key: 'rendering',     header: 'Rendering'         },
  { key: 'enc_dt',        header: 'Enc Dt'            },
  // srvcs_no is the flattening anchor — buildAuditExportRows expands it into:
  // Billed CPT | Audited CPT | Billed Modifier | Audited Modifier | Units Billed | Units Audited
  { key: 'srvcs_no',      header: 'Billed CPT'        },
  // billed_icd_* inserted dynamically after flattening columns
  // audited_icd_* inserted dynamically after billed ICDs
  { key: 'notes',         header: 'Notes'             },
  { key: 'response',      header: 'Response'          },
  // dynamic extra columns (Provider Education, etc.) appended at end
];

// ─── Internal helpers ─────────────────────────────────────────────────────────

function getCptUnits(code) {
  if (!code) return [undefined, undefined];
  const parts = code.split('*');
  return parts.length > 1 ? [parts[0], parts[1]] : [parts[0], 1];
}

function buildCptDict(key, keyPrefix, dict, row) {
  if (!key.startsWith(keyPrefix)) return false;
  const splits = key.split('_');
  const cptId = parseInt(splits.length === 3 ? splits[2] : '1', 10);
  dict[cptId] = row[key];
  return true;
}

const NOTES_DELIMITERS = ['.', ';'];

function parseNotes(notes, cptList) {
  const CPTtoNotesMap = {};
  cptList.forEach((cpt) => { if (cpt) CPTtoNotesMap[cpt] = ''; });

  let commonNotes = '';
  if (!notes) return { CPTtoNotesMap, commonNotes };

  for (const note of notes.split(new RegExp(`[${NOTES_DELIMITERS.join('')}]`))) {
    if (!note) continue;
    const tokens = note.split(' ');
    const matchedCPTs = new Set();
    for (const token of tokens) {
      if (token in CPTtoNotesMap) matchedCPTs.add(token);
    }
    for (const cpt of matchedCPTs) {
      CPTtoNotesMap[cpt] += `${note}. `;
    }
    if (!matchedCPTs.size) commonNotes += `${note}. `;
  }

  return { CPTtoNotesMap, commonNotes };
}

// ─── Column index helpers (same logic as the original inline code) ─────────────

function buildColumnMeta(columnOrder, headers) {
  // indices to omit
  const omittingIndices = [];
  columnOrder.forEach((val, idx) => {
    if (OMITTING_KEYS.includes(val)) { omittingIndices.push(idx); return; }
    for (const prefix of FLATTENING_KEYS) {
      if (val.startsWith(prefix)) { omittingIndices.push(idx); break; }
    }
  });

  let filteredColumnOrder = columnOrder.filter((_, idx) => !omittingIndices.includes(idx));
  let filteredHeaders     = headers.filter((_, idx) => !omittingIndices.includes(idx));

  let firstFlatteningIndex = -1;
  let last_audited_icd     = -1;
  let notes_pos            = -1;

  filteredColumnOrder.forEach((val, idx) => {
    if (STARTING_KEYS.includes(val))      firstFlatteningIndex = idx;
    if (val.startsWith('audited_icd'))    last_audited_icd     = idx;
    if (val === 'notes')                  notes_pos            = idx;
  });

  firstFlatteningIndex += 1;

  // Move notes after last audited ICD
  if (last_audited_icd !== -1 && notes_pos !== -1) {
    filteredColumnOrder = [...filteredColumnOrder];
    filteredColumnOrder.splice(last_audited_icd + 1, 0, 'notes');
    filteredColumnOrder.splice(notes_pos, 1);

    filteredHeaders = [...filteredHeaders];
    filteredHeaders.splice(last_audited_icd + 1, 0, 'Notes');
    filteredHeaders.splice(notes_pos, 1);
  }

  // Insert flattened CPT headers
  const finalHeaders = [...filteredHeaders];
  finalHeaders.splice(firstFlatteningIndex, 0, ...FLATTENED_CSV_HEADERS);

  return { filteredColumnOrder, finalHeaders, firstFlatteningIndex };
}

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * Transforms raw audit sheet source data into a 2-D array of rows
 * suitable for CSV output or Excel cell population.
 *
 * @param {object[]} sourceData        - raw rows from Handsontable getSourceData() or the sheet prop
 * @param {object[]} columnData        - column definition array ({ data, ... })
 * @param {string[]} headers           - display header labels (same order as columnData)
 * @param {object}   responseAuditIssues - issue map used to resolve response IDs to names
 * @param {Function} generateOptions   - (row, issueMap) => [{id, name}, ...]
 *
 * @returns {{ headers: string[], rows: string[][] }}
 *   headers - the final header row (strings)
 *   rows    - each element is an array of cell strings, one per CPT expansion
 */
export function buildAuditExportRows({
  sourceData,
  columnData,
  headers,
  responseAuditIssues,
  generateOptions,
}) {
  const columnOrder = columnData.map((c) => c.data);

  // ── resolve response IDs → display names ──
  const processedData = sourceData
    .map((row) => {
      if (row.id === null) return null;
      const newRow = { ...row };
      const responseValue = newRow.response;
      if (responseValue && responseAuditIssues) {
        newRow.response = decodeResponseValue(responseValue, row, responseAuditIssues);
      }
      return newRow;
    })
    .filter(Boolean);

  const { filteredColumnOrder, finalHeaders, firstFlatteningIndex } =
    buildColumnMeta(columnOrder, headers);

  // ── build rows ──
  const dataRows = processedData.flatMap((row) => {
    if (typeof row !== 'object' || row === null) return [];

    // Build per-CPT dictionaries for each flattening key
    const flatteningKeyValue = Array.from({ length: FLATTENING_KEYS.length }, () => ({}));

    Object.keys(row).forEach((key) => {
      for (const [idx, prefix] of FLATTENING_KEYS.entries()) {
        if (buildCptDict(key, prefix, flatteningKeyValue[idx], row)) break;
      }
    });

    const maxCPTs = Math.max(
      ...Object.keys(flatteningKeyValue[0]),
      ...Object.keys(flatteningKeyValue[1]),
    );

    // Build the flattened list: [[billedCPT, auditedCPT, billedMod, auditedMod, unitsBilled, unitsReviewed], ...]
    const flatteningValueList = [];
    for (let idx = 1; idx <= maxCPTs; idx++) {
      if (!flatteningKeyValue[0][idx] && !flatteningKeyValue[1][idx]) continue;

      let units_list = [];
      const flattenedItem = FLATTENING_KEYS.map((_, i) => {
        if (i === 0 || i === 1) {
          const ret = getCptUnits(flatteningKeyValue[i][idx]);
          units_list.push(ret[1]);
          return ret[0];
        }
        const ret_list = [flatteningKeyValue[i][idx]];
        if (i === 3) ret_list.push(...units_list);
        return ret_list;
      }).flat();

      flatteningValueList.push(flattenedItem);
    }

    // Parse notes
    const cptList = flatteningValueList.map((el) => el[0]);
    const { CPTtoNotesMap, commonNotes } = parseNotes(row.notes, cptList);

    // Expand one output row per CPT
    return flatteningValueList.map((flattenedItem) => {
      return filteredColumnOrder.flatMap((key, idx) => {
        if (idx === firstFlatteningIndex) {
          // inject the 6 flattened CPT columns then the regular column value
          const regularValue = getCellValue(row, key, flattenedItem, CPTtoNotesMap, commonNotes);
          return [...flattenedItem, regularValue];
        }
        return [getCellValue(row, key, flattenedItem, CPTtoNotesMap, commonNotes)];
      });
    });
  });

  return { headers: finalHeaders, rows: dataRows };
}

function getCellValue(row, key, flattenedItem, CPTtoNotesMap, commonNotes) {
  if (key === 'enc_dt') {
    // Preserve date formatting (Excel will get the raw string; CSV wraps in ="...")
    return row['enc_dt'] ?? '';
  }
  if (key === 'notes') {
    const cpt = flattenedItem[0];
    return cpt
      ? (CPTtoNotesMap[cpt] ?? '') + commonNotes
      : commonNotes;
  }
  return row[key] ?? '';
}

export async function fetchAndPrepareAuditSheetRows(uploadId) {
  const [response, responseAuditIssues] = await Promise.all([
    auditSheetRepository.getAuditSheet(uploadId),
    auditSheetRepository.getResponseAuditIssues(),
  ]);

  if (!response || response.error || !response.sheets?.length) return null;
  const res = response.sheets[0];
  const _reDestructuredData = createEmptyColumn(res.data);

  const reDestructuredData = _reDestructuredData.map(sd => {
    const tempObj = { ...sd, ...sd.additional_attributes };
    tempObj['audited_cpt'] = tempObj['audited_code'];
    delete tempObj.additional_attributes;
    delete tempObj.audited_code;

    if (tempObj.enc_dt) {
      const parts = tempObj.enc_dt.split('-');
      const year  = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day   = parseInt(parts[2], 10);
      const parsed = new Date(year, month, day);
      if (!isNaN(parsed.getTime())) {
        tempObj.enc_dt = format(parsed, 'MM-dd-yyyy');
      }
    }

    if (tempObj.rendering && typeof tempObj.rendering === 'object') {
      tempObj.rendering = toTitleCase(
        `${tempObj.rendering.first_name} ${tempObj.rendering.last_name}`
      );
    }

    if (!tempObj.hasOwnProperty('icd_agree'))    tempObj.icd_agree    = null;
    if (!tempObj.hasOwnProperty('icd_disagree')) tempObj.icd_disagree = null;

    // Resolve response IDs → display names
    if (tempObj.response) {
      tempObj.response = decodeResponseValue(tempObj.response, tempObj, responseAuditIssues);
    }

    return tempObj;
  });

  const arrangedData = arrangeData(reDestructuredData);

  const firstRow = arrangedData.find(r => r.id != null) ?? arrangedData[0];
  if (!firstRow) return null;

  const allKeys = Object.keys(firstRow);

  // ── Dynamic ICD columns from actual row keys ──
  const billedIcds  = allKeys.filter(k => k.startsWith('billed_icd')).sort();
  const auditedIcds = allKeys.filter(k => k.startsWith('audited_icd')).sort();

  // ── Extra dynamic columns (Provider Education, Number & Complexity, etc.) ──
  // Anything not in OMITTING_KEYS, not a flattening key, not already covered
  const coveredKeys = new Set([
    ...OMITTING_KEYS,
    ...FLATTENING_KEYS,
    'file_name', 'encounter_no', 'rendering', 'enc_dt',
    'notes', 'response',
    ...billedIcds, ...auditedIcds,
  ]);
  const extraKeys = allKeys.filter(k => !coveredKeys.has(k));

  // ── Build final ordered columnData + headers ──
  const columnData = [];
  const headers    = [];

  const push = (key, header) => {
    // Only include if the key actually exists in the row
    if (allKeys.includes(key) || key === 'srvcs_no') {
      columnData.push({ data: key });
      headers.push(header);
    }
  };

  // Fixed columns up to flattening anchor
  push('file_name',    'File Id #');
  push('encounter_no', 'Encounter #');
  push('rendering',    'Rendering');
  push('enc_dt',       'Enc Dt');
  push('srvcs_no',     'Billed CPT'); // anchor; buildAuditExportRows expands this

  // Dynamic ICD columns
  billedIcds.forEach((k, i)  => push(k, i === 0 ? 'Billed ICD'  : `Billed ICD${i}`));
  auditedIcds.forEach((k, i) => push(k, i === 0 ? 'Audited ICD' : `Audited ICD${i}`));

  // Notes + Response
  push('notes',    'Notes');
  push('response', 'Response');

  // Extra dynamic columns keep their key-derived names
  extraKeys.forEach(k => {
    columnData.push({ data: k });
    headers.push(k.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '));
  });

  return { arrangedData, columnData, headers };
}